import { Form } from "antd";
import { getMsg } from "../../utils/antdStatic";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { configOptionApi } from "../../services/configOption.api";
import multiavatar from "@multiavatar/multiavatar";
import { useEffect, useRef, useState } from "react";
import { useSidebar } from "../../hooks/useSidebar";
import { isAdminRole } from "../../utils/roles.utils";
import type { AvatarChoice } from "./avatarPicker";
import { clavesCambiadas } from "../../utils/camposCambiados";
import { telefonoParaGuardar } from "../../utils/telefonoE164";
import { marcarCampoTelefono } from "../../utils/telefonoErrorApi";
import { irAlPrimerError, llevarCampoALaVista } from "../../utils/formularioErrores";
import {
  CAMPO_CONTRASENA_ACTUAL,
  MENSAJE_CONTRASENA_REQUERIDA,
  cambiaElCorreo,
  marcarCampoContrasenaActual,
} from "../../utils/cambioDeCorreoPropio";
import type { ErrorApi } from "../../services/apiClient";

/**
 * Qué pasó al pulsar «Guardar cambios» en Mi perfil. La página decide con esto qué anuncia y qué conserva:
 *   · "guardado"    — el servidor aceptó todo: se anuncia el éxito y se apaga la barra de «cambios sin guardar».
 *   · "parcial"     — se guardó lo demás pero el teléfono NO se pudo quitar (el servidor lo conservó): el hook ya avisó UNA vez, no
 *                     se anuncia éxito, y la barra se apaga porque lo que queda en pantalla es lo que hay guardado.
 *   · "sin_cambios" — nada difería de lo cargado: el hook avisó «No hay cambios que guardar.» y no se mandó nada.
 *   · "no_aplicado" — lo ÚNICO que se pidió (vaciar el teléfono) el servidor no lo aplicó: el hook avisó UNA vez; el campo y la
 *                     barra quedan como estaban (hay un cambio pendiente de verdad).
 *   · "fallo"       — validación, rechazo del servidor, error de red o doble clic: no se anuncia nada y se conserva lo escrito.
 *   · "reingreso"   — se cambió el correo y el servidor cerró las sesiones de la cuenta (`requireReLogin`): el hook ya avisó y salió
 *                     de la sesión; la página no tiene nada más que anunciar.
 */
export type ResultadoGuardarPerfil = "guardado" | "parcial" | "sin_cambios" | "no_aplicado" | "fallo" | "reingreso";

export const usePerfilLogic = () => {
  const { user, updateUser, logout } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [isAseador, setIsAseador] = useState(false);
  const [isAdminOrCliente, setIsAdminOrCliente] = useState(false);
  const [form] = Form.useForm();
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const [editMode, setEditMode] = useState(false);
  const [avatarType, setAvatarType] = useState<"upload" | "avatar">("avatar");
  const [avatarSeed, setAvatarSeed] = useState<string>(
    Math.random().toString(36).substring(2)
  );
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  // Evita que un doble clic en «Guardar cambios» mande el perfil dos veces (el `ref` corta al instante; el estado del botón lo
  // maneja la página con su propio `saving`).
  const guardandoPerfilRef = useRef(false);

  const openAvatarModal = () => setMostrarModal(true);
  const closeAvatarModal = () => setMostrarModal(false);

  // Persiste la elección del picker de avatar INMEDIATAMENTE (no espera al
  // botón "Guardar cambios" del formulario, que solo aparece si cambiaste un
  // campo de texto — antes un cambio de solo-avatar no se guardaba nunca).
  // Recibe la elección explícita desde el modal para no depender de estado
  // asíncrono (evita la carrera del viejo `submitProfile`). Los toasts de
  // éxito/error los emite `apiFetch` vía successMsg/errorMsg.
  const persistAvatar = async (choice: AvatarChoice) => {
    if (!userData?.id) return;
    setSavingAvatar(true);
    try {
      let ok = false;
      if (choice.type === "upload") {
        const url = await api.usersApi.uploadUserAvatar(userData.id, choice.file);
        if (url) {
          setAvatarUrl(url);
          setAvatarType("upload");
          setAvatarFile(choice.file);
          // Sincroniza el AuthContext para que el avatar del header y del
          // sidebar cambien EN VIVO (comparten `user.avatar/avatar_url`).
          // Solo uno de los dos queda populado (invariante del backend).
          updateUser({ avatar_url: url, avatar: "" });
          ok = true;
        }
      } else {
        const s = await api.usersApi.updateUserAvatarSeed(userData.id, choice.seed);
        if (s) {
          setAvatarSeed(s);
          setAvatarType("avatar");
          setAvatarUrl(null);
          setAvatarFile(null);
          updateUser({ avatar: s, avatar_url: "" });
          ok = true;
        }
      }
      if (ok) setMostrarModal(false);
    } finally {
      setSavingAvatar(false);
    }
  };

  const handleSelectSeed = (seed: string) => {
    setAvatarType("avatar");
    setAvatarSeed(seed);
  };

  const handleUploadFile = (file: File) => {
    setAvatarType("upload");
    setAvatarFile(file);
  };

  const submitProfile = async () => {
    try {
      let avatarUrl: string | null = null;

      if (avatarType === "upload" && avatarFile) {
        // tu wrapper espera un File
        avatarUrl = await api.usersApi.uploadUserAvatar(
          userData.id,
          avatarFile
        );
        setAvatarUrl(avatarUrl);
      } else {
        // necesitas otro endpoint que acepte sólo seed
        await api.usersApi.updateUserAvatarSeed(userData.id, avatarSeed);
        setAvatarSeed(avatarSeed);
        avatarUrl = null;
      }

      getMsg()?.success("Avatar actualizado");
      closeAvatarModal();
      return avatarUrl;
    } catch (e) {
      console.error(e);
      getMsg()?.error("Error al actualizar avatar");
      return null;
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (!user) {
        getMsg()?.error("No se pudo cargar la información del usuario.");
        return;
      }
      const userData = await api.usersApi.getUser(user.id);
      setUserData(userData);
      if (!userData) {
        getMsg()?.error("No se pudo cargar la información del usuario.");
        return;
      }
      // Rol escalar post-refactor (defensivo ante array legacy).
      const rol = Array.isArray(userData.rol)
        ? (userData.rol as unknown as string[])[0]
        : ((userData.rol as unknown as string) ?? "");
      setRoles([rol].filter(Boolean));
      setIsAseador(rol === "aseador");
      setIsAdminOrCliente(isAdminRole(rol) || rol === "cliente");

      if (userData.avatar_url) {
        // Ya había una imagen subida
        setAvatarUrl(userData.avatar_url);
      } else if (userData.avatar) {
        // Tenías una semilla guardada
        setAvatarSeed(userData.avatar);
      } else {
        // Primera vez: fallback desde email
        const fallback = userData.email || "usuario";
        setAvatarSeed(fallback);
      }
    };

    fetchUser();
  }, [user]);

  const generateNewAvatar = () => {
    const newSeed = Math.random().toString(36).substring(2, 10);
    setAvatarSeed(multiavatar(newSeed));
    setAvatarSeed(newSeed);
  };

  // El rechazo del servidor se marca además junto al campo: el teléfono (409 duplicado, 400 formato) o, si se estaba cambiando el
  // correo, la contraseña actual (400 requerida, 403 incorrecta, 429 demasiados intentos). Un 401 NO pasa por acá: el interceptor de
  // `apiClient` ya cierra la sesión.
  const alFallarPerfil = (cambiaCorreo: boolean) => (error: ErrorApi) => {
    if (marcarCampoTelefono(form, "telefono", error)) return;
    if (cambiaCorreo) marcarCampoContrasenaActual(form, error);
  };

  /**
   * Guarda el perfil y dice qué pasó (ver `ResultadoGuardarPerfil`). Solo "guardado" anuncia el éxito: si falló (teléfono repetido,
   * correo ya usado, error del servidor) o el servidor no aplicó lo único que se pidió, no se anuncia nada y se conserva lo escrito
   * para que la persona lo corrija (antes decía «Perfil actualizado correctamente» aunque el servidor lo hubiera rechazado).
   */
  const handleUpdate = async (): Promise<ResultadoGuardarPerfil> => {
    // El candado se toma ANTES de validar: `validateFields` es asíncrono y dos clics seguidos entrarían los dos.
    if (guardandoPerfilRef.current) return "fallo";
    guardandoPerfilRef.current = true;
    try {
      let values: any;
      try {
        values = await form.validateFields();
      } catch (errorValidacion) {
        // Los errores de cada campo ya quedaron marcados en el formulario; falta llevar a la persona hasta el primero: la barra de
        // «Guardar cambios» está fija abajo y el campo con error puede estar arriba, fuera de la pantalla del celular.
        irAlPrimerError(form, errorValidacion);
        return "fallo";
      }

      // Solo viaja lo que la persona CAMBIÓ respecto de lo que cargó la pantalla: reenviar todo pisaría con datos viejos lo que
      // otra persona (un administrador) haya corregido mientras tanto, y obligaba a completar datos que no se estaban tocando.
      const original: Record<string, unknown> = {
        nombre: userData?.nombre_usuario,
        email: userData?.email_usuario,
        telefono: userData?.telefono_usuario,
        descripcion: userData?.descripcion,
        rut: userData?.rut_quien_recibe,
        banco: userData?.banco_quien_recibe,
        tipocuenta: userData?.tipocuenta_quien_recibe,
        numerocuenta: userData?.numerocuenta_quien_recibe,
      };
      const cambiados = clavesCambiadas(original, values, [
        "nombre",
        "email",
        "telefono",
        "descripcion",
        "rut",
        "banco",
        "tipocuenta",
        "numerocuenta",
      ]);
      if (cambiados.length === 0) {
        // Igual que en Administradores y /aseadores: no se manda nada y se dice por qué (antes la página anunciaba un éxito falso).
        getMsg()?.info("No hay cambios que guardar.");
        return "sin_cambios";
      }

      // `sector` NO se envía: lo asigna la administración (round 2026-10-28). El backend lo ignoraría igual si llegara de una
      // aseadora.
      const CAMPO_API = {
        nombre: "nombre_usuario",
        email: "email_usuario",
        telefono: "telefono_usuario",
        descripcion: "descripcion",
        rut: "rut_quien_recibe",
        banco: "banco_quien_recibe",
        tipocuenta: "tipocuenta_quien_recibe",
        numerocuenta: "numerocuenta_quien_recibe",
      } as const;
      const updateData: Record<string, unknown> = {};
      for (const k of cambiados) {
        // Vaciar el teléfono se manda como null (explícito); nunca se borra por un valor que no se entiende.
        updateData[CAMPO_API[k]] = k === "telefono" ? telefonoParaGuardar(values[k]) : values[k];
      }

      // Cambiar el correo de MI cuenta exige la contraseña actual (S-03). Se compara como lo hace el servidor (sin espacios ni
      // mayúsculas): un cambio solo de mayúsculas no es un cambio y no la pide. Si el correo no cambió, la contraseña ni se pide ni
      // se envía aunque la persona la haya escrito antes de devolver el correo al original.
      const cambiaCorreo = cambiados.includes("email") && cambiaElCorreo(original.email, values.email);
      if (cambiaCorreo) {
        const contrasena = values[CAMPO_CONTRASENA_ACTUAL];
        if (typeof contrasena !== "string" || contrasena === "") {
          // La página siempre muestra el campo cuando el correo cambia; esto es la red de seguridad (sin él el servidor respondería 400).
          form.setFields([{ name: CAMPO_CONTRASENA_ACTUAL, errors: [MENSAJE_CONTRASENA_REQUERIDA] }]);
          // Con el desplazamiento y el despeje de la barra fija, no solo el foco (ronda F3b).
          llevarCampoALaVista(form, CAMPO_CONTRASENA_ACTUAL);
          return "fallo";
        }
        updateData.currentPassword = contrasena;
      }

      // `sinAvisoDeExito`: el aviso de éxito lo decide este hook DESPUÉS de mirar la respuesta. Si lo diera `apiFetch`, un perfil en
      // que el servidor ignoró lo único que se pidió mostraba «Usuario actualizado correctamente» y, encima, la advertencia.
      const result = await api.usersApi.updateUser(userData.id, updateData, { alFallar: alFallarPerfil(cambiaCorreo), sinAvisoDeExito: true });

      // `updateUser` (apiFetch) ya mostró el motivo REAL que dio el servidor, p. ej. el 409 TELEFONO_DUPLICADO
      // («Ese teléfono ya lo usa otra persona del equipo…»). Un segundo toast genérico encima no aporta nada y,
      // con el tope de 3 avisos de la app, puede sacar de la pantalla el que explica qué pasó. Se queda en edición.
      if (!result) return "fallo";

      // La contraseña ya cumplió su función: no se queda escrita en el formulario (si el servidor la rechazó, la marca del campo ya
      // la dejó como corresponde y no se llega hasta acá).
      // El correo cambió y el servidor cerró las sesiones de la cuenta (`requireReLogin`, como el cambio de contraseña): el token que
      // sigue en uso ya no sirve y cualquier otra llamada daría 401. Se avisa claro y se sale de la sesión; la persona entra con el
      // correo nuevo. (Si algún día el servidor deja de pedirlo, la sesión sigue y se actualiza el AuthContext como siempre.)
      form.setFieldValue(CAMPO_CONTRASENA_ACTUAL, undefined);
      if (cambiaCorreo && (result as { requireReLogin?: boolean })?.requireReLogin === true) {
        logout();
        // `logout()` ya anunció «Sesión cerrada correctamente» (verde): junto al nuestro, /login mostraba DOS avisos verdes seguidos
        // (ronda F3b). Se quita el genérico y queda el que explica POR QUÉ se cerró y qué hacer. Va DESPUÉS de `logout()`: la
        // cola de avisos procesa en orden, así que `destroy` borra el genérico y el nuestro entra limpio.
        getMsg()?.destroy?.();
        getMsg()?.success("Correo actualizado. Por seguridad se cerró tu sesión: vuelve a iniciar sesión con tu nuevo correo.");
        return "reingreso";
      }

      // Pidió vaciar el teléfono y el servidor lo conservó (hoy ignora el `null`; `data.telefono` trae el que quedó). Anunciar éxito
      // sería mentir: la pantalla diría una cosa y la base tendría otra.
      const guardadoPorElServidor = (result as { data?: { telefono?: string | null } })?.data?.telefono;
      const noSeQuitoElTelefono = cambiados.includes("telefono") && updateData.telefono_usuario === null && !!guardadoPorElServidor;
      if (noSeQuitoElTelefono && cambiados.length === 1) {
        // Lo único que se pidió no se aplicó. UN solo mensaje; el campo y la barra de cambios quedan como estaban (el cambio sigue
        // pendiente: «Descartar» devuelve el número que hay guardado).
        getMsg()?.warning("No se pudo quitar el teléfono: sigue guardado el anterior.");
        return "no_aplicado";
      }
      if (noSeQuitoElTelefono) {
        // Lo demás sí se guardó: la pantalla pasa a mostrar el teléfono que QUEDÓ y se avisa, una vez, por qué no coincide.
        values.telefono = guardadoPorElServidor;
        form.setFieldValue("telefono", guardadoPorElServidor);
      }

      // La pantalla recuerda lo que acaba de guardar como la nueva base de comparación.
      setUserData((previo: any) =>
        previo
          ? {
              ...previo,
              ...Object.fromEntries(
                cambiados.map((k) => [CAMPO_API[k], k === "telefono" ? values.telefono || null : values[k]]),
              ),
            }
          : previo,
      );

      // Sync AuthContext so sidebar and other consumers reflect updated data
      updateUser({
        nombre: values.nombre,
        email: values.email,
        telefono: values.telefono,
      });

      setEditMode(false);
      if (noSeQuitoElTelefono) {
        getMsg()?.warning("Se guardaron los demás cambios, pero no se pudo quitar el teléfono: sigue guardado el anterior.");
        return "parcial";
      }
      getMsg()?.success("Perfil actualizado correctamente");
      return "guardado";
    } catch (error) {
      getMsg()?.error("No se pudo actualizar el perfil");
      return "fallo";
    } finally {
      guardandoPerfilRef.current = false;
    }
  };

  const avatarOptions = ["lion", "panda", "dragon", "sky", "fire", "ghost"];

  const [availableBanks, setAvailableBanks] = useState<string[]>([]);
  const [accountTypes, setAccountTypes] = useState<string[]>([]);

  useEffect(() => {
    const fetchOptions = async () => {
      const [bancosRes, cuentasRes] = await Promise.all([
        configOptionApi.list("banco"),
        configOptionApi.list("tipocuenta"),
      ]);
      setAvailableBanks(bancosRes?.data?.map((o) => o.valor) ?? []);
      setAccountTypes(cuentasRes?.data?.map((o) => o.valor) ?? []);
    };
    fetchOptions();
  }, []);
  return {
    userData,
    roles,
    isAseador,
    isAdminOrCliente,
    form,
    availableBanks,
    accountTypes,
    sidebarOpen,
    editMode,
    avatarSeed,
    avatarUrl,
    avatarOptions,
    avatarType,
    avatarFile,
    mostrarModal,
    openAvatarModal,
    closeAvatarModal,
    handleSelectSeed,
    handleUploadFile,
    submitProfile,
    persistAvatar,
    savingAvatar,
    setEditMode,
    setAvatarSeed,
    handleUpdate,
    toggleSidebar,
    generateNewAvatar,
  };
};
